import React from 'react';

import { eventStore } from '../../services/EventStore';
import { debugLog } from '../debug/debugLog';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import type { OnlineSnapshot } from '../../types/snapshots';
import type { OnlineServerMessage } from '../../types/transport';
import type { GameState, GameEvent } from '../../types/game';
import type { GameAction } from '../gameReducer';

const SERVER_ERROR_RESYNC_CODES = new Set([
  'ACTIVE_PLAYER_MISMATCH',
  'ROLL_INDEX_OUT_OF_SYNC',
  'KEEP_REQUIRED_BEFORE_ROLL',
  'PLAYER_NOT_ACTIVE',
]);

/** Bicie serca ma świadczyć o żywym połączeniu, nie kronikować każdego zdarzenia. */
const ONLINE_HEARTBEAT_LOG_INTERVAL_MS = 5_000;

type SnapshotHydrationResult = 'applied' | 'already-current' | 'ignored';

const parseSequenceNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const extractSequenceNumber = (event: GameEvent): number | null => {
  return parseSequenceNumber((event as { sequenceNumber?: unknown }).sequenceNumber);
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export function useOnlineSnapshotHydration({
  gameState,
  dispatch,
}: {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
}) {
  const refreshInFlightRef = React.useRef(false);
  const bootstrapSnapshotRef = React.useRef<string | null>(null);
  const lastAppliedSnapshotAtRef = React.useRef(0);
  const lastAppliedSnapshotIdentityRef = React.useRef<string | null>(null);
  const lastAutoRefreshAtByReasonRef = React.useRef<Map<string, number>>(new Map());
  const lastServerErrorRefreshAtByCodeRef = React.useRef<Map<string, number>>(new Map());
  const latestRouteStateRef = React.useRef<{ gameId: string; mode: GameState['mode'] }>({
    gameId: gameState.id,
    mode: gameState.mode,
  });
  const latestSequenceRef = React.useRef(0);
  const lastOnlineEventAtRef = React.useRef(0);
  const lastHeartbeatLogAtRef = React.useRef(0);
  const lastOnlineStatusRef = React.useRef<string | null>(null);

  const syncLog = React.useCallback((label: string, payload: Record<string, unknown>) => {
    if (isVerboseLoggingEnabled()) {
      logger.debug(`SYNC_${label}`, payload);
    }
    debugLog(`sync:${label}`, payload, { source: 'useGameEffects' });
  }, []);

  const log = React.useCallback((label: string, payload: unknown) => {
    debugLog(label, payload, { source: 'useGameEffects' });
  }, []);

  React.useEffect(() => {
    latestRouteStateRef.current = {
      gameId: gameState.id,
      mode: gameState.mode,
    };
  }, [gameState.id, gameState.mode]);

  React.useEffect(() => {
    lastAppliedSnapshotAtRef.current = 0;
    lastAppliedSnapshotIdentityRef.current = null;
  }, [gameState.id]);

  React.useEffect(() => {
    const sequences = (gameState.events || [])
      .map((ev) => extractSequenceNumber(ev))
      .filter((n): n is number => n != null);
    const maxSeq = sequences.length > 0 ? Math.max(...sequences) : 0;
    if (maxSeq > latestSequenceRef.current) {
      latestSequenceRef.current = maxSeq;
    }
  }, [gameState.events]);

  const hydrateSnapshotIfFresh = React.useCallback((snapshot: OnlineSnapshot, meta: { reason: string; source: string }): SnapshotHydrationResult => {
    const currentGameId = latestRouteStateRef.current.gameId;
    const currentMode = latestRouteStateRef.current.mode;
    if (currentMode === 'REAL') {
      syncLog('SNAPSHOT_SKIP', {
        reason: meta.reason,
        skip: 'mode-real',
        source: meta.source,
        gameId: currentGameId,
      });
      return 'ignored';
    }
    if (!snapshot.gameId || snapshot.gameId !== currentGameId) {
      syncLog('SNAPSHOT_SKIP', {
        reason: meta.reason,
        skip: 'game-mismatch',
        source: meta.source,
        currentGameId,
        snapshotGameId: snapshot.gameId || null,
      });
      return 'ignored';
    }

    const snapshotIdentity = [
      snapshot.gameId,
      snapshot.updatedAt || '',
      String(snapshot.turn),
      snapshot.activePlayerId || '',
      String(snapshot.rollIndex),
    ].join('|');
    if (snapshotIdentity === lastAppliedSnapshotIdentityRef.current) {
      syncLog('SNAPSHOT_SKIP', {
        reason: meta.reason,
        skip: 'same-identity',
        source: meta.source,
        gameId: snapshot.gameId,
      });
      return 'already-current';
    }

    const parsedUpdatedAt = Date.parse(snapshot.updatedAt || '');
    if (Number.isFinite(parsedUpdatedAt) && parsedUpdatedAt <= lastAppliedSnapshotAtRef.current) {
      syncLog('SNAPSHOT_SKIP', {
        reason: meta.reason,
        skip: 'stale-or-equal-updated-at',
        source: meta.source,
        gameId: snapshot.gameId,
        snapshotUpdatedAt: snapshot.updatedAt || null,
      });
      return 'already-current';
    }

    syncLog('SNAPSHOT_APPLY', {
      reason: meta.reason,
      source: meta.source,
      gameId: snapshot.gameId,
      turn: snapshot.turn,
      updatedAt: snapshot.updatedAt,
    });
    dispatch({ type: 'HYDRATE_FROM_SNAPSHOT', snapshot });
    if (Number.isFinite(parsedUpdatedAt) && parsedUpdatedAt > lastAppliedSnapshotAtRef.current) {
      lastAppliedSnapshotAtRef.current = parsedUpdatedAt;
    }
    lastAppliedSnapshotIdentityRef.current = snapshotIdentity;
    return 'applied';
  }, [dispatch, syncLog]);

  const refreshSnapshot = React.useCallback(async (reason: string) => {
    if (!gameState.id) {
      syncLog('REFRESH_SKIPPED', { reason, skip: 'no-game-id', mode: gameState.mode });
      return false;
    }
    if (refreshInFlightRef.current) {
      syncLog('REFRESH_SKIPPED', { reason, skip: 'in-flight', gameId: gameState.id, mode: gameState.mode });
      return false;
    }
    const requestedGameId = gameState.id;
    const requestedMode = gameState.mode;
    syncLog('REFRESH_REQUESTED', { reason, gameId: requestedGameId, mode: requestedMode });
    refreshInFlightRef.current = true;
    try {
      if (requestedMode === 'ONLINE') {
        const snap = await eventStore.getSnapshot(requestedGameId);
        if (!snap) {
          syncLog('SNAPSHOT_SKIP', { reason, skip: 'not-found', source: 'rpc', gameId: requestedGameId });
          return false;
        }
        if (latestRouteStateRef.current.gameId !== requestedGameId || latestRouteStateRef.current.mode !== requestedMode) {
          syncLog('SNAPSHOT_SKIP', {
            reason,
            skip: 'stale-route',
            source: 'rpc',
            requestedGameId,
            requestedMode,
          });
          if (isVerboseLoggingEnabled()) {
            logger.info('SNAPSHOT_REFRESH_SKIPPED_STALE', { reason, requestedGameId, requestedMode });
          }
          return false;
        }
        if (snap.gameId && snap.gameId !== requestedGameId) {
          syncLog('SNAPSHOT_SKIP', {
            reason,
            skip: 'game-mismatch',
            source: 'rpc',
            requestedGameId,
            snapshotGameId: snap.gameId,
          });
          if (isVerboseLoggingEnabled()) {
            logger.warn('SNAPSHOT_REFRESH_GAME_MISMATCH', {
              reason,
              requestedGameId,
              snapshotGameId: snap.gameId,
            });
          }
          return false;
        }
        const hydrationResult = hydrateSnapshotIfFresh(snap as OnlineSnapshot, { reason, source: 'rpc' });
        if (hydrationResult === 'ignored') return false;
      } else {
        syncLog('REFRESH_SKIPPED', { reason, skip: 'unsupported-mode', gameId: requestedGameId, mode: requestedMode });
        return false;
      }
      if (isVerboseLoggingEnabled()) {
        logger.info('SNAPSHOT_REFRESHED', { reason, gameId: requestedGameId, mode: requestedMode });
      }
      return true;
    } catch (error) {
      syncLog('REFRESH_FAILED', { reason, gameId: requestedGameId, mode: requestedMode, error: getErrorMessage(error) });
      if (isVerboseLoggingEnabled()) {
        logger.warn('SNAPSHOT_REFRESH_FAILED', { reason, error: getErrorMessage(error) });
      }
      return false;
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [gameState.id, gameState.mode, hydrateSnapshotIfFresh, syncLog]);

  const handleOnlineEventApplied = React.useCallback((event: GameEvent) => {
    lastOnlineEventAtRef.current = Date.now();
    const sequence = extractSequenceNumber(event);
    if (sequence != null && sequence > latestSequenceRef.current) {
      latestSequenceRef.current = sequence;
    }
    // To jest bicie serca, nie dziennik zdarzeń: per-zdarzeniowy wpis powtarzałby
    // `APPLY_EVENT` co do bajta, a w grze samych botów bez opóźnień robi z tego
    // tysiąc wierszy szumu. Refy aktualizujemy zawsze, log zostawiamy rzadki.
    const now = lastOnlineEventAtRef.current;
    if (now - lastHeartbeatLogAtRef.current < ONLINE_HEARTBEAT_LOG_INTERVAL_MS) return;
    lastHeartbeatLogAtRef.current = now;
    syncLog('ONLINE_HEARTBEAT', {
      gameId: latestRouteStateRef.current.gameId,
      eventType: event.type,
      sequence: sequence ?? null,
      at: now,
    });
  }, [syncLog]);

  const handleOnlineStatusChange = React.useCallback((status: string) => {
    lastOnlineStatusRef.current = status;
    syncLog('ONLINE_STATUS', {
      gameId: latestRouteStateRef.current.gameId,
      status,
      lastEventAt: lastOnlineEventAtRef.current || null,
    });
  }, [syncLog]);

  const handleOnlineSnapshot = React.useCallback((snapshot: OnlineSnapshot) => {
    hydrateSnapshotIfFresh(snapshot, { reason: 'online', source: 'transport' });
  }, [hydrateSnapshotIfFresh]);

  const handleOnlineServerError = React.useCallback((message: Extract<OnlineServerMessage, { type: 'ERROR' }>) => {
    const current = latestRouteStateRef.current;
    if (current.mode !== 'ONLINE' || !current.gameId) {
      syncLog('SERVER_ERROR_SKIP', {
        gameId: current.gameId || null,
        code: message.code,
        skip: 'mode-not-online',
      });
      return;
    }
    if (!SERVER_ERROR_RESYNC_CODES.has(message.code)) {
      syncLog('SERVER_ERROR_SKIP', {
        gameId: current.gameId,
        code: message.code,
        skip: 'non-resync-code',
      });
      return;
    }

    const now = Date.now();
    const throttledMs = 2_000;
    const throttleKey = `${current.gameId}:${message.code}`;
    const lastTriggeredAt = lastServerErrorRefreshAtByCodeRef.current.get(throttleKey) ?? 0;
    if (now - lastTriggeredAt < throttledMs) {
      syncLog('SERVER_ERROR_SKIP', {
        gameId: current.gameId,
        code: message.code,
        skip: 'throttled',
        elapsedMs: now - lastTriggeredAt,
      });
      return;
    }
    lastServerErrorRefreshAtByCodeRef.current.set(throttleKey, now);
    const normalizedCode = message.code.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const reason = `server-error-${normalizedCode}`;
    syncLog('SERVER_ERROR_RESYNC', {
      gameId: current.gameId,
      code: message.code,
      reason,
      commandType: message.commandType ?? null,
      eventId: message.eventId ?? null,
    });
    void refreshSnapshot(reason);
  }, [refreshSnapshot, syncLog]);

  const requestAutoRefresh = React.useCallback((reason: 'visibility' | 'focus' | 'app-resume') => {
    const now = Date.now();
    const throttledMs = 2_000;
    const onlineFreshMs = 1_500;
    const lastAutoRefreshAt = lastAutoRefreshAtByReasonRef.current.get(reason) ?? 0;
    if (now - lastAutoRefreshAt < throttledMs) {
      syncLog('REFRESH_SKIPPED', { reason, skip: 'throttled', elapsedMs: now - lastAutoRefreshAt });
      return;
    }
    const lastOnlineEventAt = lastOnlineEventAtRef.current;
    if (lastOnlineEventAt > 0 && now - lastOnlineEventAt < onlineFreshMs) {
      syncLog('REFRESH_SKIPPED', {
        reason,
        skip: 'online-fresh',
        elapsedMs: now - lastOnlineEventAt,
      });
      return;
    }
    lastAutoRefreshAtByReasonRef.current.set(reason, now);
    void refreshSnapshot(reason);
  }, [refreshSnapshot, syncLog]);

  React.useEffect(() => {
    if (gameState.mode === 'REAL' || !gameState.id) return;
    if (bootstrapSnapshotRef.current === gameState.id) return;
    bootstrapSnapshotRef.current = gameState.id;
    void refreshSnapshot('bootstrap');
  }, [gameState.id, gameState.mode, refreshSnapshot]);

  return {
    log,
    syncLog,
    refreshSnapshot,
    requestAutoRefresh,
    handleOnlineEventApplied,
    handleOnlineStatusChange,
    handleOnlineSnapshot,
    handleOnlineServerError,
    lastOnlineEventAtRef,
    lastOnlineStatusRef,
  };
}
